export default function NMTCJourneyBanner() {
  return (
    <div className="space-y-12">

      {/* ── What is NMTC? ──────────────────────────────────── */}
      <div className="text-center max-w-2xl mx-auto space-y-4">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-snug">
          Your project costs more than you can finance.
          <br />
          <span className="text-emerald-700">NMTC closes that gap.</span>
        </h2>
        <p className="text-gray-500 text-lg leading-relaxed">
          The New Markets Tax Credit program turns federal tax credits into
          below-market financing for projects in underserved communities —
          covering <strong className="text-gray-700">15–25%</strong> of total
          project costs.
        </p>
      </div>

      {/* ── Before / After visual ──────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
        {/* Without */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-base font-bold">✕</span>
            <h3 className="font-bold text-gray-800 text-base">Without NMTC</h3>
          </div>
          <div className="space-y-3">
            <Bar label="Your Equity" pct={20} color="bg-gray-400" />
            <Bar label="Bank Loan" pct={55} color="bg-gray-400" />
            <Bar label="Funding Gap" pct={25} color="bg-red-300" dashed />
          </div>
          <p className="text-sm text-gray-400">Project stalls — not enough capital.</p>
        </div>
        {/* With */}
        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-6 space-y-4">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-base font-bold">✓</span>
            <h3 className="font-bold text-gray-800 text-base">With NMTC</h3>
          </div>
          <div className="space-y-3">
            <Bar label="Your Equity" pct={20} color="bg-gray-400" />
            <Bar label="Bank Loan" pct={55} color="bg-gray-400" />
            <Bar label="NMTC Benefit" pct={25} color="bg-emerald-500" />
          </div>
          <p className="text-sm text-emerald-700 font-medium">Gap filled — project moves forward.</p>
        </div>
      </div>

      {/* ── Who qualifies (plain English) ──────────────────── */}
      <div className="max-w-3xl mx-auto">
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="font-bold text-gray-800 text-base mb-4">Who can use NMTC?</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Qualifier
              icon="📍"
              text="Located in a low-income community"
            />
            <Qualifier
              icon="🏗️"
              text="Real estate or business project over ~$3M"
            />
            <Qualifier
              icon="🏥"
              text="Healthcare, manufacturing, retail, nonprofit, mixed-use & more"
            />
          </div>
        </div>
      </div>

      {/* ── How it works (3 simple steps) ──────────────────── */}
      <div className="max-w-3xl mx-auto">
        <h3 className="font-bold text-gray-800 text-base mb-5 text-center">How it works</h3>
        <div className="grid sm:grid-cols-3 gap-5">
          <Step num={1} title="Check eligibility" desc="Is your project in a qualifying area? We'll help you find out." />
          <Step num={2} title="Structure the deal" desc="Tax credits are sold to an investor. The proceeds become low-cost financing for your project." />
          <Step num={3} title="Build & repay" desc="Use the funds. After 7 years, a portion of the debt is forgiven — that's your net benefit." />
        </div>
      </div>

      {/* ── Explore prompt ─────────────────────────────────── */}
      <div className="text-center">
        <p className="text-sm text-gray-400">
          Use the tabs above to size a deal, see the structure, and calculate your benefit.
        </p>
      </div>
    </div>
  );
}


/* ── Tiny helper components ────────────────────────────────── */

function Bar({ label, pct, color, dashed }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-500 w-24 shrink-0 text-right">{label}</span>
      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color} ${dashed ? 'opacity-40 bg-stripes' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function Qualifier({ icon, text }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="text-xl leading-none mt-0.5">{icon}</span>
      <span className="text-sm text-gray-600 leading-snug">{text}</span>
    </div>
  );
}

function Step({ num, title, desc }) {
  return (
    <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 space-y-2.5">
      <div className="flex items-center gap-2.5">
        <span className="w-7 h-7 rounded-full bg-emerald-700 text-white text-sm font-bold flex items-center justify-center shrink-0">
          {num}
        </span>
        <h4 className="font-bold text-gray-800 text-base">{title}</h4>
      </div>
      <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
    </div>
  );
}
